import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import MetricsTable from './MetricsTable'
import { useEvalRunForm } from './EvalRunFormContext'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: '1px solid var(--color-neutral-200)',
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    color: 'var(--color-neutral-600)',
    flexShrink: 0
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  modelLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  modelSelect: {
    padding: '3px 8px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    fontFamily: 'inherit',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  body: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    padding: '12px',
    gap: '14px',
    minHeight: 0
  },
  rubricSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: '160px'
  },
  metricsSection: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '4px'
  },
  label: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  charCount: {
    fontSize: '10px',
    color: 'var(--color-neutral-400)'
  },
  rubric: {
    flex: 1,
    width: '100%',
    padding: '10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    boxSizing: 'border-box',
    resize: 'none',
    fontFamily: 'inherit',
    minHeight: '160px',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  }
}))

function JudgePanel ({ modelOptions }) {
  const classes = useStyles()
  const { rubric, setRubric, judgeModelId, setJudgeModelId } = useEvalRunForm()

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <span>Judge setup</span>
        <div className={classes.headerRight}>
          <span className={classes.modelLabel}>Model</span>
          <select
            className={classes.modelSelect}
            value={judgeModelId}
            onChange={(e) => setJudgeModelId(e.target.value)}
            title='Falls back to the eval_judge task mapping, then default_task'
          >
            <option value=''>Default</option>
            {modelOptions.map(m => (
              <option key={m.modelId} value={m.modelId}>{m.displayName || m.modelId}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.rubricSection}>
          <div className={classes.labelRow}>
            <span className={classes.label}>Judge prompt / rubric</span>
            <span className={classes.charCount}>{rubric.length} chars</span>
          </div>
          <textarea
            className={classes.rubric}
            value={rubric}
            onChange={(e) => setRubric(e.target.value)}
            placeholder='How should the judge model evaluate the assistant response?'
          />
        </div>

        <div className={classes.metricsSection}>
          <div className={classes.labelRow}>
            <span className={classes.label}>Metrics</span>
          </div>
          <MetricsTable />
        </div>
      </div>
    </div>
  )
}

JudgePanel.propTypes = {
  modelOptions: PropTypes.array.isRequired
}

export default JudgePanel
